// features/ignoreOverlaps.tsx
import { FloorPlanData } from './types';

/**
 * Add overlap pairs to the ignored list
 * @param floorPlanData Current floor plan data
 * @param overlapsToIgnore Array of [roomId1, roomId2] pairs to ignore
 * @returns Updated floor plan data with ignored overlaps
 */
export function addIgnoredOverlaps(
  floorPlanData: FloorPlanData,
  overlapsToIgnore: string[][]
): FloorPlanData {
  const currentIgnored = floorPlanData.ignoredOverlaps || [];
  
  // Normalize pairs to avoid duplicates (always store [smaller, larger] lexicographically)
  const normalizedNew = overlapsToIgnore.map(([id1, id2]) => {
    return id1 < id2 ? [id1, id2] : [id2, id1];
  });
  
  // Filter out duplicates
  const newIgnored = normalizedNew.filter(([id1, id2]) => {
    return !currentIgnored.some(([existingId1, existingId2]) => {
      return (
        (id1 === existingId1 && id2 === existingId2) ||
        (id1 === existingId2 && id2 === existingId1)
      );
    });
  });
  
  return {
    ...floorPlanData,
    ignoredOverlaps: [...currentIgnored, ...newIgnored],
  };
}

/**
 * Remove specific overlap pairs from ignored list
 * @param floorPlanData Current floor plan data
 * @param overlapsToRemove Array of [roomId1, roomId2] pairs to remove from ignored
 * @returns Updated floor plan data
 */
export function removeIgnoredOverlaps(
  floorPlanData: FloorPlanData,
  overlapsToRemove: string[][]
): FloorPlanData {
  const currentIgnored = floorPlanData.ignoredOverlaps || [];
  
  const filtered = currentIgnored.filter(([ignoredId1, ignoredId2]) => {
    return !overlapsToRemove.some(([id1, id2]) => {
      return (
        (id1 === ignoredId1 && id2 === ignoredId2) ||
        (id1 === ignoredId2 && id2 === ignoredId1)
      );
    });
  });
  
  return {
    ...floorPlanData,
    ignoredOverlaps: filtered,
  };
}

/**
 * Clear all ignored overlaps
 * @param floorPlanData Current floor plan data
 * @returns Updated floor plan data with no ignored overlaps
 */
export function clearAllIgnoredOverlaps(
  floorPlanData: FloorPlanData
): FloorPlanData {
  return {
    ...floorPlanData,
    ignoredOverlaps: [],
  };
}

/**
 * Check if a specific overlap is ignored
 * @param floorPlanData Current floor plan data
 * @param roomId1 First room ID
 * @param roomId2 Second room ID
 * @returns True if this overlap is ignored
 */
export function isOverlapIgnored(
  floorPlanData: FloorPlanData,
  roomId1: string,
  roomId2: string
): boolean {
  const ignoredOverlaps = floorPlanData.ignoredOverlaps || [];
  
  return ignoredOverlaps.some(([ignoredId1, ignoredId2]) => {
    return (
      (roomId1 === ignoredId1 && roomId2 === ignoredId2) ||
      (roomId1 === ignoredId2 && roomId2 === ignoredId1)
    );
  });
}

/**
 * Clean up ignored overlaps by removing entries for deleted rooms
 * Call this after deleting rooms to keep the ignored list clean
 * @param floorPlanData Current floor plan data
 * @returns Updated floor plan data with cleaned ignored overlaps
 */
export function cleanupIgnoredOverlaps(
  floorPlanData: FloorPlanData
): FloorPlanData {
  const ignoredOverlaps = floorPlanData.ignoredOverlaps || [];
  const roomIds = new Set(floorPlanData.rooms.map(room => room.id));
  
  const cleaned = ignoredOverlaps.filter(([id1, id2]) => {
    return roomIds.has(id1) && roomIds.has(id2);
  });
  
  return {
    ...floorPlanData,
    ignoredOverlaps: cleaned,
  };
}